import base64 from 'react-native-base64';
import { firebase } from '../config/configureFirebase';
import store from '../store/store';
import AccessKey from './AccessKey';

const DEFAULT_TIMEOUT_MS = 20000;

export const functionsHost = () => {
  const { config } = firebase;
  const settings = store.getState().settingsdata.settings;
  const origin = typeof window !== 'undefined' && window.location && window.location.origin;
  if (origin && settings && settings.CompanyWebsite === origin) {
    return origin;
  }
  return `https://${config.projectId}.web.app`;
};

const networkMessage = () => {
  const lang = store.getState().languagedata.defaultLanguage || {};
  return lang.network_error || 'Connexion impossible. Vérifiez votre réseau et réessayez.';
};

// Appel d'une Cloud Function HTTP. Ne lève jamais : renvoie toujours un objet JSON.
// En cas d'échec réseau/timeout/réponse invalide : { error: <message>, code: 'network' }.
export const callFunction = async (path, body, options = {}) => {
  const { auth, basicAuth, method = 'POST', timeout = DEFAULT_TIMEOUT_MS } = options;
  const headers = { 'Content-Type': 'application/json' };
  if (basicAuth) {
    headers.Authorization = 'Basic ' + base64.encode(firebase.config.projectId + ':' + AccessKey);
  }
  if (auth !== false && firebase.auth && firebase.auth.currentUser) {
    try {
      headers['X-Firebase-Token'] = await firebase.auth.currentUser.getIdToken();
    } catch (e) {
      // Token indisponible : l'endpoint décidera.
    }
  }
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;
  try {
    const response = await fetch(`${functionsHost()}/${path}`, {
      method,
      headers,
      body: method === 'GET' ? undefined : JSON.stringify(body || {}),
      signal: controller ? controller.signal : undefined,
    });
    const text = await response.text();
    let json;
    try {
      json = text ? JSON.parse(text) : {};
    } catch (e) {
      return { error: networkMessage(), code: 'bad_response' };
    }
    if (!response.ok && !json.error) {
      json.error = networkMessage();
      json.code = json.code || 'http_' + response.status;
    }
    return json;
  } catch (error) {
    return { error: networkMessage(), code: 'network' };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// Convertit toute forme d'erreur (Firebase, chaîne, objet) en message affichable.
export const errorMessage = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  const lang = store.getState().languagedata.defaultLanguage || {};
  const map = {
    'auth/invalid-credential': lang.wrong_email_password || 'E-mail ou mot de passe incorrect.',
    'auth/wrong-password': lang.wrong_email_password || 'E-mail ou mot de passe incorrect.',
    'auth/user-not-found': lang.wrong_email_password || 'E-mail ou mot de passe incorrect.',
    'auth/invalid-email': 'Adresse e-mail invalide.',
    'auth/too-many-requests': 'Trop de tentatives. Réessayez dans quelques minutes.',
    'auth/network-request-failed': networkMessage(),
    'auth/user-disabled': 'Ce compte a été désactivé. Contactez le support.',
    'auth/account-exists-with-different-credential': 'Un compte existe déjà avec cet e-mail. Connectez-vous avec votre mot de passe.',
  };
  if (error.code && map[error.code]) return map[error.code];
  if (typeof error.error === 'string') return error.error;
  if (typeof error.message === 'string') return error.message;
  return networkMessage();
};

// Supprime récursivement les valeurs undefined (Firebase RTDB refuse toute écriture qui en contient).
export const stripUndefined = (value) => {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    Object.keys(value).forEach((k) => {
      if (value[k] !== undefined) out[k] = stripUndefined(value[k]);
    });
    return out;
  }
  return value;
};
